import { type ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { usePageTitle } from '@/features/pageTitle';
import { PageContainer } from '@/shared/ui/PageContainer';
import { CustomTypography } from '@/shared/ui/Typography';

import { AiAnalyticsReportPanel } from './ai-analytics-report.js';
import {
  AnalyticsRoot,
  AnalyticsTabButton,
  AnalyticsTabs,
  BarTrack,
  Bars,
  BottomGrid,
  ChannelDot,
  ChannelList,
  ChannelName,
  ChannelRow,
  ChannelTop,
  CustomRange,
  DataState,
  DayLabel,
  DayRow,
  DayValue,
  EmptyText,
  FilterControls,
  FilterRow,
  FunnelBar,
  FunnelList,
  FunnelMeta,
  FunnelRow,
  FunnelTrack,
  InboundBar,
  KpiGrid,
  Legend,
  LegendItem,
  MainGrid,
  MetricCard,
  MetricHeader,
  MetricSuffix,
  MetricValue,
  OutboundBar,
  Panel,
  PanelHeader,
  PeriodButton,
  PeriodButtons,
  ProgressBar,
  ProgressTrack,
  RangeErrorState,
  RangeField,
  RecommendationIndex,
  RecommendationItem,
  RecommendationList,
  WorkloadChart,
} from './analytics-page.styles.js';
import {
  fetchAnalyticsReport,
  fetchAiAnalyticsReport,
  formatMetricValue,
  formatNumber,
  isAiAnalyticsReport,
  isAnalyticsReport,
  type Kpi,
} from './analytics-api.js';
import {
  daysAgo,
  getCustomRangeDays,
  getPeriodRange,
  isAiAnalyticsRangeTooWide,
  periodOptions,
  type PeriodKey,
  today,
} from './analytics-period.js';

type AnalyticsTab = 'operations' | 'ai';

export const AnalyticsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const [customFrom, setCustomFrom] = useState(daysAgo(13));
  const [customTo, setCustomTo] = useState(today());

  usePageTitle(t('common:menu.analytics'));

  const activeTab: AnalyticsTab =
    searchParams.get('tab') === 'ai' ? 'ai' : 'operations';
  const companyId = searchParams.get('company_id')?.trim() ?? '';
  const customRangeDays = useMemo(
    () => getCustomRangeDays(customFrom, customTo),
    [customFrom, customTo]
  );
  const isCustomRangeInvalid = period === 'custom' && customRangeDays === null;
  const reportRange = useMemo(
    () => getPeriodRange(period, customFrom, customTo),
    [period, customFrom, customTo]
  );
  const isAiRangeTooWide =
    activeTab === 'ai' &&
    reportRange !== null &&
    isAiAnalyticsRangeTooWide(reportRange);
  const isAiCompanyMissing = activeTab === 'ai' && companyId === '';
  const analyticsQuery = useQuery({
    queryKey: ['analytics', reportRange?.from, reportRange?.to],
    queryFn: () => fetchAnalyticsReport(reportRange!),
    enabled: activeTab === 'operations' && reportRange !== null,
  });
  const aiAnalyticsQuery = useQuery({
    queryKey: [
      'ai-analytics-report',
      companyId,
      reportRange?.from,
      reportRange?.to,
    ],
    queryFn: () =>
      fetchAiAnalyticsReport({
        companyId,
        range: reportRange!,
      }),
    enabled:
      activeTab === 'ai' &&
      !isAiCompanyMissing &&
      !isAiRangeTooWide &&
      reportRange !== null,
  });

  const report = isAnalyticsReport(analyticsQuery.data)
    ? analyticsQuery.data
    : undefined;
  const aiReport = isAiAnalyticsReport(aiAnalyticsQuery.data)
    ? aiAnalyticsQuery.data
    : undefined;
  const kpis: Kpi[] = report
    ? [
        {
          key: 'dialogs',
          titleKey: 'dialogs',
          value: report.kpi.dialogs,
          tone: 'primary',
        },
        {
          key: 'firstResponse',
          titleKey: 'firstResponse',
          value: report.kpi.firstResponseMinutes,
          suffixKey: 'minutes',
          tone: 'success',
        },
        {
          key: 'conversion',
          titleKey: 'conversion',
          value: report.kpi.conversionPercent,
          suffixKey: 'percent',
          tone: 'violet',
        },
        {
          key: 'missed',
          titleKey: 'missed',
          value: report.kpi.missed,
          tone: 'warning',
        },
      ]
    : [];
  const maxDailyValue = Math.max(
    1,
    ...(report?.workload ?? []).map(({ inbound, outbound }) =>
      Math.max(inbound, outbound)
    )
  );
  const funnelTotal = Math.max(1, report?.funnel.incoming ?? 0);
  const funnelSteps = report
    ? [
        {
          key: 'incoming',
          value: report.funnel.incoming,
          percent: 100,
        },
        {
          key: 'answered',
          value: report.funnel.answered,
          percent: Math.round((report.funnel.answered / funnelTotal) * 100),
        },
        {
          key: 'qualified',
          value: report.funnel.qualified,
          percent: Math.round((report.funnel.qualified / funnelTotal) * 100),
        },
        {
          key: 'dealCreated',
          value: report.funnel.dealCreated,
          percent:
            report.funnel.dealCreated === null
              ? 0
              : Math.round((report.funnel.dealCreated / funnelTotal) * 100),
        },
      ]
    : [];
  const handleTabChange = (nextTab: AnalyticsTab) => {
    const nextParams = new URLSearchParams(searchParams);

    if (nextTab === 'ai') {
      nextParams.set('tab', 'ai');
    } else {
      nextParams.delete('tab');
    }

    setSearchParams(nextParams, { replace: true });
  };

  return (
    <PageContainer>
      <AnalyticsRoot>
        <FilterRow>
          <AnalyticsTabs
            aria-label={t('common:analyticsPage.tabs.label')}
            role="tablist"
          >
            <AnalyticsTabButton
              aria-selected={activeTab === 'operations'}
              role="tab"
              selected={activeTab === 'operations'}
              type="button"
              onClick={() => handleTabChange('operations')}
            >
              {t('common:analyticsPage.tabs.operations')}
            </AnalyticsTabButton>
            <AnalyticsTabButton
              aria-selected={activeTab === 'ai'}
              role="tab"
              selected={activeTab === 'ai'}
              type="button"
              onClick={() => handleTabChange('ai')}
            >
              {t('common:analyticsPage.tabs.ai')}
            </AnalyticsTabButton>
          </AnalyticsTabs>

          <FilterControls>
            <PeriodButtons direction="row" spacing="8px">
              {periodOptions.map((item) => (
                <PeriodButton
                  key={item}
                  type="button"
                  variant={period === item ? 'contained' : 'outlined'}
                  onClick={() => setPeriod(item)}
                >
                  {t(`common:analyticsPage.periods.${item}`)}
                </PeriodButton>
              ))}
            </PeriodButtons>

            {period === 'custom' ? (
              <CustomRange>
                <RangeField
                  size="small"
                  type="date"
                  label={t('common:analyticsPage.customRange.from')}
                  value={customFrom}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setCustomFrom(event.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  error={isCustomRangeInvalid}
                />
                <RangeField
                  size="small"
                  type="date"
                  label={t('common:analyticsPage.customRange.to')}
                  value={customTo}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setCustomTo(event.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  error={isCustomRangeInvalid}
                  helperText={
                    isCustomRangeInvalid
                      ? t('common:analyticsPage.customRange.error')
                      : undefined
                  }
                />
              </CustomRange>
            ) : null}
          </FilterControls>
        </FilterRow>

        {reportRange === null ? (
          <RangeErrorState>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.customRange.emptyTitle')}
            </CustomTypography>
            <CustomTypography variant="body1">
              {t('common:analyticsPage.customRange.emptyDescription')}
            </CustomTypography>
          </RangeErrorState>
        ) : activeTab === 'ai' ? (
          <AiAnalyticsReportPanel
            isError={aiAnalyticsQuery.isError}
            isLoading={aiAnalyticsQuery.isLoading}
            missingCompany={isAiCompanyMissing}
            rangeTooWide={isAiRangeTooWide}
            report={aiReport}
          />
        ) : analyticsQuery.isLoading ? (
          <DataState>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.state.loading')}
            </CustomTypography>
          </DataState>
        ) : analyticsQuery.isError || !report ? (
          <RangeErrorState>
            <CustomTypography component="h2" variant="h6">
              {t('common:analyticsPage.state.errorTitle')}
            </CustomTypography>
            <CustomTypography variant="body1">
              {t('common:analyticsPage.state.errorDescription')}
            </CustomTypography>
          </RangeErrorState>
        ) : (
          <>
            <KpiGrid>
              {kpis.map((kpi) => (
                <MetricCard key={kpi.key} tone={kpi.tone}>
                  <MetricHeader direction="row">
                    <CustomTypography variant="body1">
                      {t(`common:analyticsPage.kpi.${kpi.titleKey}`)}
                    </CustomTypography>
                  </MetricHeader>
                  <MetricValue component="div" variant="h5">
                    {formatMetricValue(kpi.value)}
                    {kpi.suffixKey && kpi.value !== null ? (
                      <MetricSuffix>
                        {t(`common:analyticsPage.units.${kpi.suffixKey}`)}
                      </MetricSuffix>
                    ) : null}
                  </MetricValue>
                </MetricCard>
              ))}
            </KpiGrid>

            <MainGrid>
              <Panel>
                <PanelHeader>
                  <CustomTypography component="h2" variant="h6">
                    {t('common:analyticsPage.workload.title')}
                  </CustomTypography>
                  <Legend direction="row" spacing="12px">
                    <LegendItem color="#0078D4">
                      {t('common:analyticsPage.workload.inbound')}
                    </LegendItem>
                    <LegendItem color="#519C66">
                      {t('common:analyticsPage.workload.outbound')}
                    </LegendItem>
                  </Legend>
                </PanelHeader>

                <WorkloadChart>
                  {report.workload.map((metric) => (
                    <DayRow key={metric.dayKey}>
                      <DayLabel variant="body2">
                        {t(`common:analyticsPage.days.${metric.dayKey}`)}
                      </DayLabel>
                      <Bars>
                        <BarTrack>
                          <InboundBar
                            style={{
                              width: `${(metric.inbound / maxDailyValue) * 100}%`,
                            }}
                          />
                        </BarTrack>
                        <BarTrack>
                          <OutboundBar
                            style={{
                              width: `${(metric.outbound / maxDailyValue) * 100}%`,
                            }}
                          />
                        </BarTrack>
                      </Bars>
                      <DayValue variant="body2">
                        {formatNumber(metric.inbound + metric.outbound)}
                      </DayValue>
                    </DayRow>
                  ))}
                </WorkloadChart>
              </Panel>

              <Panel>
                <PanelHeader>
                  <CustomTypography component="h2" variant="h6">
                    {t('common:analyticsPage.channels.title')}
                  </CustomTypography>
                </PanelHeader>

                {report.channels.length > 0 ? (
                  <ChannelList>
                    {report.channels.map((channel) => (
                      <ChannelRow key={channel.id}>
                        <ChannelTop direction="row">
                          <ChannelName>
                            <ChannelDot color={channel.color} />
                            <CustomTypography variant="body1">
                              {channel.name}
                            </CustomTypography>
                          </ChannelName>
                          <CustomTypography variant="body1">
                            {formatNumber(channel.value)}
                          </CustomTypography>
                        </ChannelTop>
                        <ProgressTrack>
                          <ProgressBar
                            color={channel.color}
                            style={{ width: `${channel.share}%` }}
                          />
                        </ProgressTrack>
                        <CustomTypography variant="body2">
                          {t('common:analyticsPage.channels.share', {
                            value: channel.share,
                          })}
                        </CustomTypography>
                      </ChannelRow>
                    ))}
                  </ChannelList>
                ) : (
                  <EmptyText variant="body1">
                    {t('common:analyticsPage.state.empty')}
                  </EmptyText>
                )}
              </Panel>
            </MainGrid>

            <BottomGrid>
              <Panel>
                <PanelHeader>
                  <CustomTypography component="h2" variant="h6">
                    {t('common:analyticsPage.funnel.title')}
                  </CustomTypography>
                </PanelHeader>

                <FunnelList>
                  {funnelSteps.map((step) => (
                    <FunnelRow key={step.key}>
                      <FunnelMeta direction="row">
                        <CustomTypography variant="body1">
                          {t(`common:analyticsPage.funnel.${step.key}`)}
                        </CustomTypography>
                        <CustomTypography variant="body1">
                          {formatMetricValue(step.value)}
                        </CustomTypography>
                      </FunnelMeta>
                      <FunnelTrack>
                        <FunnelBar style={{ width: `${step.percent}%` }} />
                      </FunnelTrack>
                    </FunnelRow>
                  ))}
                </FunnelList>
              </Panel>

              <Panel>
                <PanelHeader>
                  <CustomTypography component="h2" variant="h6">
                    {t('common:analyticsPage.recommendations.title')}
                  </CustomTypography>
                </PanelHeader>

                {report.recommendations.length > 0 ? (
                  <RecommendationList>
                    {report.recommendations.map((item, index) => (
                      <RecommendationItem key={item}>
                        <RecommendationIndex>{index + 1}</RecommendationIndex>
                        <CustomTypography variant="body1">
                          {t(`common:analyticsPage.recommendations.${item}`)}
                        </CustomTypography>
                      </RecommendationItem>
                    ))}
                  </RecommendationList>
                ) : (
                  <EmptyText variant="body1">
                    {t('common:analyticsPage.state.noRecommendations')}
                  </EmptyText>
                )}
              </Panel>
            </BottomGrid>
          </>
        )}
      </AnalyticsRoot>
    </PageContainer>
  );
};
